import React, { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"

import DataTable from "components/Tables/DataTable"
import Delete from "components/Modal/Delete"
import Modal from "components/Modal/MaterialModal"
import InputLabel from "components/Input/LabelInput"

import { Button, Input, Label } from "reactstrap"
import PropTypes from "prop-types"
import { useFormik } from "formik"
import * as Yup from "yup"

import {
  saveTablasGenerales,
  deleteTablasGenerales,
  recuperarTablaGeneral,
  saveFilaTablaExterna,
  deleteFilaTablaExterna,
  recuperarFilaTablaExterna,
  getTablaGeneralConfig,
  getTablasGeneralesByNro,
  getFilasTablaExterna,
  deleteCatalogo,
  recuperarCatalogo,
} from "api"
import toastHandler from "toastHandler"
import userStore from "userStore"
import ConfigCatalogoModal from "./ConfigCatalogoModal"

const esVacio = v => v === null || v === undefined || v === ""

const esValorTrue = v => v === true || v === 1 || v === "1" || v === "true"

const pad2 = n => String(n).padStart(2, "0")

const numeroEsAR = (v, opts) => {
  if (esVacio(v)) return ""
  const n = Number(v)
  return isNaN(n) ? v : n.toLocaleString("es-AR", opts)
}

// Convierte un valor datetime (ISO u otro) al formato requerido por <input type="datetime-local">
const toDatetimeLocalValue = v => {
  if (esVacio(v)) return ""
  const d = new Date(v)
  if (isNaN(d.getTime())) return ""
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

const formatters = {
  numerico: v => numeroEsAR(v),
  entero: v => numeroEsAR(v, { maximumFractionDigits: 0 }),
  monto: v =>
    numeroEsAR(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  mayuscula: v => (esVacio(v) ? "" : String(v).toUpperCase()),
  minuscula: v => (esVacio(v) ? "" : String(v).toLowerCase()),
  porcentaje: v => (esVacio(v) ? "" : `${v}%`),
  periodoAAAAMM: v => {
    const s = String(v ?? "")
    return s.length === 6 ? `${s.slice(4, 6)}/${s.slice(0, 4)}` : v
  },
  moneda: v =>
    numeroEsAR(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  booleano: v => {
    const marcado = esValorTrue(v)
    return (
      <i
        className={`mdi font-size-18 ${
          marcado ? "mdi-check-bold text-success" : "mdi-close text-muted"
        }`}
        title={marcado ? "Sí" : "No"}
      />
    )
  },
  datetime: v => {
    if (esVacio(v)) return ""
    const d = new Date(v)
    if (isNaN(d.getTime())) return v
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  },
}

const formatValue = (formato, value) =>
  formato && formatters[formato] ? formatters[formato](value) : value

const byOrden = (a, b) => (a.orden || 0) - (b.orden || 0)

const ROW_STYLES = [
  {
    when: row => row.activo === false,
    style: { color: "#adb5bd", fontStyle: "italic" },
  },
]

const TIPO_CATALOGO_LABEL = {
  NATIVA: "Tabla Interna",
  COMPONENTE_EXTERNO: "Basada en Componente",
  TABLA_EXTERNA: "Tabla Externa",
}

const MODO_BORRADO_ICON = {
  F: {
    className: "mdi-delete-forever-outline text-danger",
    title: "Borrado físico (definitivo)",
  },
  L: {
    className: "mdi-archive-outline text-secondary",
    title: "Borrado lógico (conserva la historia)",
  },
  N: {
    className: "mdi-lock text-dark",
    title: "Protegido (no se puede borrar)",
  },
}

const TablasGenerales = ({
  tablas,
  nro,
  config,
  action,
  setAction,
  incluirInactivos,
  setIncluirInactivos,
}) => {
  const history = useHistory()
  const [row, setRow] = useState({})
  const [modal, setModal] = useState(false)

  const { setSuccess, setError } = toastHandler()

  const toggle = () => setModal(!modal)

  const user = userStore(state => state.user)
  const permisos = userStore(state => state.permisos)
  const esAdmin = userStore(state => state.admin)
  const [catalogoConfig, setCatalogoConfig] = useState(null)
  const [campoFocused, setCampoFocused] = useState(null)
  const [busquedaCatalogo, setBusquedaCatalogo] = useState("")
  const [filtroTipoCatalogo, setFiltroTipoCatalogo] = useState("")
  const [catalogoAEliminar, setCatalogoAEliminar] = useState(null)
  const [valoresFiltroCampos, setValoresFiltroCampos] = useState({})

  const soloLectura = row.activo === false

  const NOMBRES_CONFIG_CATALOGOS = [
    "configuración de catálogos",
    "configuracion de catalogos",
  ]
  const permisoConfig = permisos?.find(p =>
    NOMBRES_CONFIG_CATALOGOS.includes((p.nombreObjeto || "").toLowerCase()),
  )
  const puedeVerConfig = esAdmin || permisoConfig?.consulta === true
  const puedeEditarConfig = esAdmin || permisoConfig?.modificacion === true

  const esNativo =
    config?.nombreComponente === null ||
    config?.nombreComponente === undefined ||
    config?.nombreComponente === "default"

  // Catálogo de tipo Tabla Externa: mismo motor de grilla/formulario genérico,
  // pero el CRUD de filas opera sobre los endpoints de tabla-externa en vez de
  // los nativos de TABLASGENERALES.
  const esTablaExterna = config?.tipoCatalogo === "TABLA_EXTERNA"

  const handleConfigClick = row => {
    if (!esNativo) {
      return
    }
    setCatalogoConfig({ ...row, readOnly: !puedeEditarConfig })
  }

  const campos = config?.campos || []
  const camposFiltrables = campos.filter(c => c.filtro).sort(byOrden)
  const camposVisiblesForm = campos.filter(c => c.visibleForm).sort(byOrden)
  const camposGrillaOrdenados = campos
    .filter(c => c.visibleGrilla)
    .sort(byOrden)
  // Preferimos una columna que no sea el autoincremental/PK (suele ser el Id,
  // poco útil como etiqueta); si no hay otra, usamos la primera igual.
  const primerCampoGrilla =
    camposGrillaOrdenados.find(c => !c.autoincremento) ||
    camposGrillaOrdenados[0]
  // Nativo siempre tiene "descripcion"; en Tabla Externa no hay garantía de ese
  // campo, así que como respaldo se usa el valor de esa columna.
  const etiquetaFila = fila =>
    fila?.descripcion ??
    (primerCampoGrilla ? fila?.[primerCampoGrilla.campo] : undefined) ??
    ""
  const formatoByCampo = campos.reduce((acc, c) => {
    acc[c.campo] = c.formato
    return acc
  }, {})

  // Campos "combo": sus opciones (label/value) salen de OTRO catálogo. Se
  // resuelven acá (no en ConfigCatalogoModal) porque son las filas reales del
  // catálogo origen, no su configuración.
  const [opcionesCombo, setOpcionesCombo] = useState({})
  useEffect(() => {
    const camposCombo = (config?.campos || []).filter(
      c => c.formato === "combo" && c.comboNroTabla,
    )
    if (camposCombo.length === 0) {
      setOpcionesCombo({})
      return
    }
    let cancelado = false
    const nroTablasOrigen = [...new Set(camposCombo.map(c => c.comboNroTabla))]
    Promise.all(
      nroTablasOrigen.map(nroOrigen =>
        getTablaGeneralConfig(nroOrigen).then(cfgOrigen => {
          const fetchFilas =
            cfgOrigen?.tipoCatalogo === "TABLA_EXTERNA"
              ? getFilasTablaExterna(nroOrigen)
              : getTablasGeneralesByNro(nroOrigen)
          return fetchFilas.then(filas => [nroOrigen, filas])
        }),
      ),
    )
      .then(resultados => {
        if (cancelado) return
        const filasPorNroTabla = Object.fromEntries(resultados)
        const nuevo = {}
        camposCombo.forEach(c => {
          const filas = filasPorNroTabla[c.comboNroTabla] || []
          nuevo[c.campo] = filas.map(fila => ({
            value: fila[c.comboCampoValor],
            label: fila[c.comboCampoEtiqueta],
          }))
        })
        setOpcionesCombo(nuevo)
      })
      .catch(setError)
    return () => {
      cancelado = true
    }
  }, [config])

  // Al entrar a otro catálogo, los filtros por campo del anterior ya no aplican.
  useEffect(() => {
    setValoresFiltroCampos({})
  }, [nro])

  const esRequerido = c =>
    c.requerido || c.campo === "codNum" || c.campo === "descripcion"

  const validationSchema = Yup.object(
    // Solo se valida sobre campos visibles en el form: un campo oculto (p.ej.
    // el id/PK) marcado como requerido bloquearía el submit sin mostrar
    // ningún error visible en la pantalla (no hay input donde mostrarlo).
    camposVisiblesForm.reduce((acc, c) => {
      if (esRequerido(c) && c.formato !== "booleano") {
        acc[c.campo] = Yup.string().required("El campo es obligatorio")
      }
      if (c.campo === "codNum") {
        acc[c.campo] = (acc[c.campo] || Yup.string()).test(
          "codigo-duplicado",
          "El código ya existe en el catálogo",
          v =>
            esVacio(v) ||
            !tablas.some(
              t => t.id !== row.id && Number(t.codNum) === Number(v),
            ),
        )
      }
      return acc
    }, {}),
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...row,
    },
    validationSchema,
    onSubmit: values => {
      values.usuario = user.username
      values.fecha = new Date()
      const guardar = esTablaExterna
        ? saveFilaTablaExterna(nro, values)
        : saveTablasGenerales(values)
      guardar
        .then(() => {
          setSuccess()
          toggle()
          setAction(!action)
        })
        .catch(setError)
    },
  })

  const columnsTablas = [
    {
      name: "",
      selector: row => {
        const icono = MODO_BORRADO_ICON[row.modoBorrado] || MODO_BORRADO_ICON.L
        return (
          <i
            className={`mdi ${icono.className} font-size-18`}
            title={icono.title}
          />
        )
      },
      width: "56px",
      center: true,
    },
    {
      name: "#Tabla",
      selector: row => row.codNum,
      sortable: true,
      sortFunction: (a, b) => (Number(a.codNum) || 0) - (Number(b.codNum) || 0),
    },
    {
      name: "Título",
      selector: row => row.titulo || row.descripcion || "",
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Descripción",
      selector: row => row.descripcion,
      sortable: true,
      minWidth: "300px",
    },
    {
      name: "Tipo",
      selector: row =>
        TIPO_CATALOGO_LABEL[row.tipoCatalogo] || row.tipoCatalogo || "",
      sortable: true,
      minWidth: "160px",
    },
    {
      name: (
        <button
          className={`mdi font-size-18 border-0 bg-transparent p-0 ${
            incluirInactivos
              ? "mdi-eye text-primary"
              : "mdi-eye-off-outline text-secondary"
          }`}
          title={
            incluirInactivos
              ? "Mostrando todos los catálogos (click para ocultar los inactivos)"
              : "Mostrando solo catálogos activos (click para ver también los inactivos)"
          }
          onClick={() => setIncluirInactivos(!incluirInactivos)}
        />
      ),
      selector: row => (
        <div className="d-flex gap-2">
          <button
            className="mdi mdi-eye-outline font-size-18 text-secondary border-0 bg-transparent p-0"
            title="Ver catálogo"
            onClick={() => addParam(row.codNum)}
          />
          {puedeVerConfig && (
            <button
              className={`mdi font-size-18 border-0 bg-transparent p-0 ${
                puedeEditarConfig
                  ? "mdi-cog-outline text-primary"
                  : "mdi-magnify text-secondary"
              }`}
              title={
                puedeEditarConfig
                  ? "Configurar catálogo"
                  : "Ver configuración (solo lectura)"
              }
              onClick={() => handleConfigClick(row)}
            />
          )}
          {puedeEditarConfig && row.activo === false && (
            <button
              className="mdi mdi-backup-restore font-size-18 text-primary border-0 bg-transparent p-0"
              title="Recuperar catálogo (volver a activo)"
              onClick={() => handleRecuperarCatalogo(row.codNum)}
            />
          )}
          {puedeEditarConfig && row.activo !== false && (
            <button
              className="mdi mdi-delete-outline font-size-18 text-danger border-0 bg-transparent p-0"
              title="Eliminar catálogo"
              onClick={() => setCatalogoAEliminar(row)}
            />
          )}
        </div>
      ),
      width: "150px",
      right: true,
    },
  ]

  const FORMATOS_NUMERICOS = [
    "numerico",
    "monto",
    "moneda",
    "porcentaje",
    "entero",
  ]

  const dataColumns = campos
    .filter(c => c.visibleGrilla)
    .sort(byOrden)
    .map(c => ({
      name: c.etiqueta || c.campo,
      sortable: true,
      selector:
        c.formato === "texto"
          ? row => <div style={{ whiteSpace: "pre-wrap" }}>{row[c.campo]}</div>
          : c.formato === "combo"
            ? row =>
                opcionesCombo[c.campo]?.find(
                  o => String(o.value) === String(row[c.campo]),
                )?.label ?? row[c.campo]
            : row => formatValue(c.formato, row[c.campo]),
      right:
        c.campo === "valorInt" ||
        c.campo === "valorFloat" ||
        FORMATOS_NUMERICOS.includes(c.formato),
      center: c.formato === "booleano",
      // "texto" es el formato de campos libres/potencialmente largos (p.ej.
      // "descripcion" en Tabla Interna, o cualquier campo de texto en un
      // catálogo de Tabla Externa): se les da más ancho/crecimiento en vez de
      // truncarlos como al resto de las columnas.
      ...(c.formato === "texto"
        ? { grow: 4, minWidth: "300px" }
        : { minWidth: "150px" }),
    }))

  const puedeEditar = config?.permiteEdicion !== false
  const puedeBorrar = config?.modoBorrado !== "N"
  const puedeAgregar = config != null && config?.permiteAlta !== false

  const codNumAutoincremental =
    campos.find(c => c.campo === "codNum")?.autoincremento === true
  const proximoCodNum = () =>
    tablas.reduce((max, t) => Math.max(max, Number(t.codNum) || 0), 0) + 1

  const ocultarInactivas = config?.ocultarInactivos && !incluirInactivos
  const filasVisibles = (
    ocultarInactivas ? tablas.filter(t => t.activo !== false) : tablas
  )
    .filter(fila =>
      camposFiltrables.every(c => {
        const valorFiltro = valoresFiltroCampos[c.campo]
        if (valorFiltro === undefined || valorFiltro === "") return true
        if (c.formato === "booleano") {
          return esValorTrue(fila[c.campo]) === (valorFiltro === "true")
        }
        if (c.formato === "combo") {
          return String(fila[c.campo]) === valorFiltro
        }
        return String(fila[c.campo] ?? "")
          .toLowerCase()
          .includes(valorFiltro.toLowerCase())
      }),
    )
    .slice()
    .sort(byOrden)

  const catalogosFiltrados = tablas.filter(t => {
    const texto = busquedaCatalogo.trim().toLowerCase()
    const coincideBusqueda =
      !texto ||
      (t.titulo || "").toLowerCase().includes(texto) ||
      (t.descripcion || "").toLowerCase().includes(texto)
    const coincideTipo =
      !filtroTipoCatalogo || t.tipoCatalogo === filtroTipoCatalogo
    return coincideBusqueda && coincideTipo
  })

  const accionesColumn = {
    name: "Acciones",
    selector: row => (
      <div className="d-flex gap-3">
        {puedeEditar && (
          <button
            className={`mdi font-size-18 border-0 bg-transparent p-0 ${
              row.activo === false
                ? "mdi-eye-outline text-secondary"
                : "mdi-pencil text-success"
            }`}
            title={row.activo === false ? "Ver (inactivo)" : "Editar"}
            onClick={() => {
              setRow(row)
              toggle()
            }}
          />
        )}
        {puedeEditar && row.activo === false && (
          <button
            className="mdi mdi-backup-restore font-size-18 text-primary border-0 bg-transparent p-0"
            title="Recuperar (volver a activo)"
            onClick={() => handleRecuperar(row.id)}
          />
        )}
        {puedeBorrar &&
          (() => {
            const inactivar =
              (config?.modoBorrado || "L") === "L" && row.activo !== false
            return (
              <Delete
                icon={
                  inactivar ? "mdi-archive-arrow-down-outline" : "mdi-delete"
                }
                linkClassName={inactivar ? "text-secondary" : "text-danger"}
                confirmColor={inactivar ? "secondary" : "danger"}
                confirmText={inactivar ? "Inactivar" : "Eliminar"}
                title={
                  inactivar
                    ? "Inactivar"
                    : row.activo === false
                      ? "Eliminar definitivamente"
                      : "Eliminar"
                }
                modalHeader={
                  inactivar
                    ? "Inactivar elemento"
                    : row.activo === false
                      ? "Eliminar definitivamente"
                      : "Eliminar elemento"
                }
                modalText={
                  inactivar
                    ? `¿Desea inactivar el elemento "${row.descripcion}"? Podrá recuperarlo más tarde.`
                    : row.activo === false
                      ? `"${row.descripcion}" ya está dada de baja. ¿Eliminarla de forma DEFINITIVA (borrado físico)? Esta acción no se puede deshacer.`
                      : `¿Está seguro que desea eliminar el elemento "${row.descripcion}"?`
                }
                onClick={() => handleDelete(row.id, !inactivar)}
              />
            )
          })()}
      </div>
    ),
    right: true,
    width: "140px",
  }

  const columnsTablasByNro =
    puedeEditar || puedeBorrar ? [...dataColumns, accionesColumn] : dataColumns

  const addParam = nro => history.push({ search: `?nro=${nro}` })

  const handleChange = e => {
    const { name, value } = e.target
    const formato = formatoByCampo[name]

    if (FORMATOS_NUMERICOS.includes(formato)) {
      let formattedValue = value.replace(/[^0-9.]/g, "")
      const parts = formattedValue.split(".")
      if (parts.length > 2) {
        formattedValue = `${parts[0]}.${parts.slice(1).join("")}`
      }
      setRow({ ...row, [name]: formattedValue })
      return
    }

    if (formato === "entero") {
      setRow({ ...row, [name]: value.replace(/[^0-9]/g, "") })
      return
    }

    setRow({ ...row, [name]: value })
  }

  const handleDelete = (id, fisico = false) => {
    const op = esTablaExterna
      ? deleteFilaTablaExterna(nro, id, fisico)
      : deleteTablasGenerales(id)
    op.then(() => {
      setSuccess()
      setAction(!action)
    }).catch(setError)
  }

  const handleRecuperar = id => {
    const op = esTablaExterna
      ? recuperarFilaTablaExterna(nro, id)
      : recuperarTablaGeneral(id)
    op.then(() => {
      setSuccess()
      setAction(!action)
    }).catch(setError)
  }

  const confirmarEliminarCatalogo = fisico => {
    deleteCatalogo(catalogoAEliminar.codNum, fisico)
      .then(() => {
        setSuccess()
        setCatalogoAEliminar(null)
        setAction(!action)
      })
      .catch(setError)
  }

  const handleRecuperarCatalogo = codNum => {
    recuperarCatalogo(codNum)
      .then(() => {
        setSuccess()
        setAction(!action)
      })
      .catch(setError)
  }

  const proximoNroCatalogo = () =>
    tablas.reduce((max, t) => Math.max(max, Number(t.codNum) || 0), 0) + 1

  return (
    <>
      {!nro && (
        <div className="d-flex justify-content-between align-items-end mb-3 gap-3 flex-wrap">
          <div className="d-flex gap-2 flex-wrap">
            <div style={{ minWidth: 260 }}>
              <Label className="mb-1">Buscar por título o descripción</Label>
              <Input
                type="search"
                bsSize="sm"
                placeholder="Buscar por título o descripción..."
                value={busquedaCatalogo}
                onChange={e => setBusquedaCatalogo(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Label className="mb-1">Tipo de catálogo</Label>
              <Input
                type="select"
                bsSize="sm"
                value={filtroTipoCatalogo}
                onChange={e => setFiltroTipoCatalogo(e.target.value)}
              >
                <option value="">Todos los tipos</option>
                <option value="NATIVA">Tabla Interna</option>
                <option value="COMPONENTE_EXTERNO">Basada en Componente</option>
                <option value="TABLA_EXTERNA">Tabla Externa</option>
              </Input>
            </div>
          </div>
          {puedeEditarConfig && (
            <Button
              color="light"
              className="shadow-none"
              onClick={() =>
                setCatalogoConfig({
                  codNum: proximoNroCatalogo(),
                  descripcion: "",
                  readOnly: false,
                })
              }
            >
              <i className="mdi mdi-plus me-1" />
              Agregar Catálogo
            </Button>
          )}
        </div>
      )}
      {nro && camposFiltrables.length > 0 && (
        <div className="d-flex gap-2 flex-wrap mb-3">
          {camposFiltrables.map(c => (
            <div key={c.campo} style={{ minWidth: 180 }}>
              <Label className="mb-1">{c.etiqueta || c.campo}</Label>
              {c.formato === "combo" ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={valoresFiltroCampos[c.campo] || ""}
                  onChange={e =>
                    setValoresFiltroCampos(prev => ({
                      ...prev,
                      [c.campo]: e.target.value,
                    }))
                  }
                >
                  <option value="">Todos</option>
                  {(opcionesCombo[c.campo] || []).map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Input>
              ) : c.formato === "booleano" ? (
                <Input
                  type="select"
                  bsSize="sm"
                  value={valoresFiltroCampos[c.campo] || ""}
                  onChange={e =>
                    setValoresFiltroCampos(prev => ({
                      ...prev,
                      [c.campo]: e.target.value,
                    }))
                  }
                >
                  <option value="">Todos</option>
                  <option value="true">Sí</option>
                  <option value="false">No</option>
                </Input>
              ) : (
                <Input
                  type="search"
                  bsSize="sm"
                  placeholder={`Filtrar por ${c.etiqueta || c.campo}...`}
                  value={valoresFiltroCampos[c.campo] || ""}
                  onChange={e =>
                    setValoresFiltroCampos(prev => ({
                      ...prev,
                      [c.campo]: e.target.value,
                    }))
                  }
                />
              )}
            </div>
          ))}
        </div>
      )}
      {nro && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            {config?.ocultarInactivos && (
              <Label check className="d-flex align-items-center gap-2 mb-0">
                <Input
                  type="checkbox"
                  checked={!!incluirInactivos}
                  onChange={e => setIncluirInactivos(e.target.checked)}
                />
                Mostrar inactivos
              </Label>
            )}
          </div>
          {puedeAgregar && (
            <Button
              color="light"
              className="shadow-none"
              onClick={() => {
                toggle()
                if (esTablaExterna) {
                  const fila = { id: null, activo: true }
                  campos.forEach(c => {
                    if (!(c.campo in fila)) fila[c.campo] = ""
                  })
                  setRow(fila)
                  return
                }
                setRow({
                  id: 0,
                  codAlfa: "",
                  codNum: codNumAutoincremental ? proximoCodNum() : null,
                  descripcion: "",
                  nroTabla: nro,
                  orden: 1,
                  valorFloat: 0,
                  valorInt: 0,
                  valorStr: "",
                  activo: true,
                  usuario: "",
                  fecha: "",
                })
              }}
            >
              Agregar
            </Button>
          )}
        </div>
      )}
      <DataTable
        columns={nro ? columnsTablasByNro : columnsTablas}
        data={nro ? filasVisibles : catalogosFiltrados}
        conditionalRowStyles={ROW_STYLES}
        showAll
      />
      <Modal
        modal={modal}
        toggle={toggle}
        modalHeader={
          row.id == 0 || row.id == null
            ? "Agregar"
            : (soloLectura ? "Ver (inactivo): " : "Editar: ") +
              etiquetaFila(tablas.find(item => item.id == row.id))
        }
      >
        <div className="mt-4 d-flex gap-3 flex-column">
          {camposVisiblesForm.map(c => {
            const esNumerico =
              FORMATOS_NUMERICOS.includes(c.formato) || c.formato === "entero"
            const valorMostrado =
              esNumerico && campoFocused !== c.campo
                ? formatValue(c.formato, row[c.campo])
                : row[c.campo]
            if (c.formato === "combo") {
              return (
                <div key={c.campo}>
                  <Label>
                    {(c.etiqueta || c.campo) + (esRequerido(c) ? " *" : "")}
                  </Label>
                  <Input
                    type="select"
                    className="font-size-13"
                    bsSize="sm"
                    name={c.campo}
                    value={row[c.campo] ?? ""}
                    onChange={handleChange}
                    invalid={!!formik.errors[c.campo]}
                    disabled={soloLectura || c.editable === false}
                  >
                    <option value="">(seleccionar)</option>
                    {(opcionesCombo[c.campo] || []).map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                  {formik.errors[c.campo] && (
                    <div className="text-danger small">
                      {formik.errors[c.campo]}
                    </div>
                  )}
                </div>
              )
            }
            if (c.formato === "booleano") {
              return (
                <div key={c.campo}>
                  <Label check className="d-flex align-items-center gap-2">
                    <Input
                      type="checkbox"
                      name={c.campo}
                      checked={esValorTrue(row[c.campo])}
                      onChange={e =>
                        setRow({ ...row, [c.campo]: e.target.checked })
                      }
                      disabled={soloLectura || c.editable === false}
                    />
                    {(c.etiqueta || c.campo) + (esRequerido(c) ? " *" : "")}
                  </Label>
                </div>
              )
            }
            if (c.formato === "datetime") {
              return (
                <div key={c.campo}>
                  <Label>
                    {(c.etiqueta || c.campo) + (esRequerido(c) ? " *" : "")}
                  </Label>
                  <Input
                    type="datetime-local"
                    name={c.campo}
                    value={toDatetimeLocalValue(row[c.campo])}
                    onChange={e =>
                      setRow({ ...row, [c.campo]: e.target.value })
                    }
                    disabled={soloLectura || c.editable === false}
                    invalid={!!formik.errors[c.campo]}
                  />
                  {formik.errors[c.campo] && (
                    <div className="text-danger small">
                      {formik.errors[c.campo]}
                    </div>
                  )}
                </div>
              )
            }
            return (
              <InputLabel
                key={c.campo}
                label={(c.etiqueta || c.campo) + (esRequerido(c) ? " *" : "")}
                name={c.campo}
                value={valorMostrado}
                onChange={handleChange}
                onFocus={
                  esNumerico ? () => setCampoFocused(c.campo) : undefined
                }
                onBlur={esNumerico ? () => setCampoFocused(null) : undefined}
                disabled={soloLectura || c.editable === false}
                invalid={!!formik.errors[c.campo]}
                feedback={formik.errors[c.campo]}
              />
            )
          })}
          <div className="d-flex justify-content-end mt-3 gap-2">
            {!soloLectura && (
              <Button
                color="primary"
                className="shadow-none"
                onClick={formik.handleSubmit}
              >
                Guardar
              </Button>
            )}
            <Button color="light" className="shadow-none" onClick={toggle}>
              {soloLectura ? "Cerrar" : "Cancelar"}
            </Button>
          </div>
        </div>
      </Modal>
      <ConfigCatalogoModal
        nro={catalogoConfig?.codNum}
        codNum={catalogoConfig?.codNum}
        descripcion={catalogoConfig?.descripcion}
        modal={catalogoConfig !== null}
        toggle={() => setCatalogoConfig(null)}
        onSaved={() => setAction(!action)}
        readOnly={catalogoConfig?.readOnly === true}
        tablas={tablas}
      />
      <Modal
        modal={catalogoAEliminar !== null}
        toggle={() => setCatalogoAEliminar(null)}
        modalHeader={`Eliminar catálogo: ${
          catalogoAEliminar?.titulo || catalogoAEliminar?.descripcion || ""
        }`}
      >
        <div className="mt-3">
          <p>¿Seguro que querés eliminar este catálogo? Elegí cómo:</p>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            <Button
              color="secondary"
              className="shadow-none"
              onClick={() => confirmarEliminarCatalogo(false)}
            >
              Inactivar (catálogo y sus ítems)
            </Button>
            <Button
              color="danger"
              className="shadow-none"
              onClick={() => confirmarEliminarCatalogo(true)}
            >
              Eliminar definitivamente
            </Button>
            <Button
              color="light"
              className="shadow-none"
              onClick={() => setCatalogoAEliminar(null)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}

TablasGenerales.propTypes = {
  tablas: PropTypes.array.isRequired,
  nro: PropTypes.any,
  config: PropTypes.object,
  loading: PropTypes.bool,
  action: PropTypes.any,
  setAction: PropTypes.func.isRequired,
  incluirInactivos: PropTypes.bool,
  setIncluirInactivos: PropTypes.func,
}

export default TablasGenerales
